/**
 * cart-drawer-v2.js — Warenkorb-Drawer, adaptiert an dieses Theme.
 *
 * Herkunft: portiert aus einem Maestrooo-Style-Theme. Die Basisklassen/Helfer
 * jenes Themes (DrawerContent, CustomHTMLElement, CustomAnimation, MediaFeatures,
 * __publicField) existieren hier NICHT und wurden ersetzt:
 *   - CartDrawer        -> extends SHTCoreDrawer (theme.js)  [Open/Close/Overlay/ESC]
 *   - LineItemQuantity  -> extends HTMLElement + eigene Event-Delegation
 *   - line-item / input-number / remove-button -> self-contained, unveraendert
 *   - CartDrawerRecommendations -> entfernt (Recommendations-Block wurde aus dem Cart genommen)
 *
 * Laufzeit-Config via window.themeVariables (wird in sections/cart-drawer-v2.liquid gesetzt).
 *
 * Stand Schritt 2.1: Oeffnen (Header-Icon -> cart:open) + Schliessen + Refresh bei
 * Mengenaenderung/Entfernen. Slide-in nutzt die vorhandene Theme-CSS (.drawer / .drawer.active
 * / .drawer__wrapper).
 */

// Dauer, fuer die eine neu erreichte Success-Message ueber der Progressbar
// stehen bleibt, bevor auf die naechste Progress-Message gewechselt wird.
const GWP_SUCCESS_MS = 3000;
// Dauer der Slide-Animation (muss zu .mini-cart__gwp-text transition in der CSS passen).
const GWP_SLIDE_MS = 300;

/* =========================================================================
 * <cart-drawer> — auf Basis der Theme-Drawer-Klasse SHTCoreDrawer
 * ========================================================================= */
class CartDrawer extends SHTCoreDrawer {
  connectedCallback() {
    if (typeof super.connectedCallback === 'function') {
      super.connectedCallback();
    }

    // Oeffnen: Header-Cart-Icon dispatched `cart:open` (siehe sections/header.liquid).
    // Beim Oeffnen immer den aktuellen Cart-Inhalt nachladen — so ist der Drawer
    // korrekt, egal was das Oeffnen ausgeloest hat (z.B. Add-to-Cart in component-add-to-cart.js).
    this._onCartOpen = (event) => {
      const opener = (event && event.detail && event.detail.opener) || document.getElementById('headerCartStatus');
      this.openDrawer(opener);
      // Frisches Oeffnen: Recommendations neu laden.
      this._rerenderCart({ preserveRecommendations: false });
    };
    // Refresh: Mengen-/Entfernen-Aktionen dispatchen `cart:refresh` auf <html>.
    // Drawer ist bereits offen -> bestehende Recommendations erhalten (nur ausblenden,
    // wenn ein Reco-Produkt inzwischen im Warenkorb liegt).
    this._onCartRefresh = () => this._rerenderCart({ preserveRecommendations: true });

    document.addEventListener('cart:open', this._onCartOpen);
    document.documentElement.addEventListener('cart:refresh', this._onCartRefresh);

    // Auto-Oeffnen nach dem /cart-Redirect (Theme-Setting cart_page_redirect_to_drawer,
    // Umleitung siehe layout/theme.liquid).
    this._maybeAutoOpenFromUrl();
  }

  /**
   * Oeffnet den Drawer, wenn die URL ?open_cart=true traegt. Der Parameter wird dabei
   * aus der Adresszeile entfernt, damit ein Reload oder Zurueck-Navigieren den Drawer
   * nicht erneut aufzieht. Geoeffnet wird ueber `cart:open` statt direkt via openDrawer(),
   * damit derselbe Pfad wie beim Header-Icon laeuft (inkl. _rerenderCart).
   */
  _maybeAutoOpenFromUrl() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    if (params.get('open_cart') !== 'true') return;

    params.delete('open_cart');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    try {
      window.history.replaceState(window.history.state, '', cleanUrl);
    } catch (error) {
      // Ohne History-API bleibt der Parameter stehen — der Drawer oeffnet trotzdem.
    }

    // Ein Frame warten: Header/Opener und die uebrigen Custom-Elements sind dann verbunden.
    requestAnimationFrame(() => document.dispatchEvent(new CustomEvent('cart:open')));
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this._onCartOpen);
    document.documentElement.removeEventListener('cart:refresh', this._onCartRefresh);
  }

  async _rerenderCart(options = {}) {
    const preserveRecommendations = options.preserveRecommendations === true;

    // Mehrfache cart:open/cart:refresh-Events koaleszieren (Add-Flow feuert mehrfach).
    if (this._rerendering) {
      // preserveRecommendations nur halten, wenn ALLE koaleszierten Events "refresh" sind;
      // ein zwischenzeitliches "open" erzwingt frisches Nachladen der Recommendations.
      if (this._rerenderPending) {
        this._rerenderPendingPreserve = this._rerenderPendingPreserve && preserveRecommendations;
      } else {
        this._rerenderPendingPreserve = preserveRecommendations;
      }
      this._rerenderPending = true;
      return;
    }
    this._rerendering = true;
    // Laufende Message-Timer abbrechen, damit schnelle Klicks keine ueberlappenden
    // Success-/Progress-Uebergaenge erzeugen (Race-Handling).
    this._clearGwpMessageTimers();
    try {
      // Optionale globale Cart-Notiz setzen (settings.activate_cart_note) — vor dem Rendern.
      if (this.dataset.note) {
        await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
          body: JSON.stringify({ note: this.dataset.note }),
          credentials: 'same-origin',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      }

      const newWrapper = await this._fetchDrawerWrapper();
      const currentWrapper = this.querySelector('.js-drawer-wrapper');

      if (newWrapper && currentWrapper) {
        // Vor dem Swap die aktuellen GWP-Step-Zustaende festhalten, um nach dem Swap
        // neu erreichte Steps zu erkennen (fuer die Konfetti-Effekte).
        const previousStepStates = this._readStepStates(currentWrapper);

        // Nur den Panel-Inhalt tauschen; das Overlay (ausserhalb des Wrappers) bleibt
        // erhalten, ebenso dessen Close-Listener. Die inneren Custom-Elemente
        // (line-item, line-item-quantity, input-number) werden automatisch neu upgraded.
        if (preserveRecommendations) {
          // Bereits geladene Recommendation-Slides in den frischen Wrapper uebernehmen,
          // bevor getauscht wird — so bleiben die geladenen Bilder erhalten (kein Flash).
          this._carryOverRecommendations(currentWrapper, newWrapper);
          // Node-basierter Swap: uebernimmt die transplantierten Live-Nodes unveraendert.
          // (Ein innerHTML-String wuerde sie neu parsen und die Bilder neu laden.)
          currentWrapper.replaceChildren(...newWrapper.childNodes);
        } else {
          // Frisches Oeffnen: kompletter Swap inkl. neu geladener Recommendations.
          currentWrapper.innerHTML = newWrapper.innerHTML;
        }
        // Close-Button lag im Wrapper -> Listener neu binden.
        this.closeBtnElement = this.querySelector('.js-drawer-btn-close');
        if (this.closeBtnElement) {
          this.closeBtnElement.addEventListener('click', this.closeDrawer.bind(this));
        }

        // Konfetti feuern, sobald eine Warenkorbaenderung einen GWP-Step erreicht hat.
        this._celebrateNewlyReachedSteps(previousStepStates);

        // Success-/Progress-Uebergang ueber der Progressbar animieren: neu erreichte
        // Steps zeigen kurz ihre Success-Message, danach kommt die naechste Progress-Message.
        const swappedWrapper = this.querySelector('.js-drawer-wrapper');
        this._animateGwpMessage(this._diffNewlyReached(previousStepStates, swappedWrapper));
      }
    } catch (error) {
      console.warn('[cart-drawer-v2] rerender failed', error);
    } finally {
      this._rerendering = false;
      if (this._rerenderPending) {
        this._rerenderPending = false;
        const pendingPreserve = this._rerenderPendingPreserve;
        this._rerenderPendingPreserve = true;
        this._rerenderCart({ preserveRecommendations: pendingPreserve });
      }
    }
  }

  /**
   * Uebernimmt die bereits geladenen Recommendation-Slides aus dem aktuellen (Live-)Slider
   * in den frisch gerenderten Wrapper, bevor getauscht wird. Dadurch werden die
   * Recommendations bei einer Warenkorb-Aenderung (cart:refresh) NICHT neu geladen —
   * kein Flash, keine erneute API-Abfrage. Gifts bleiben serverfrisch (Schwellen aendern
   * sich mit dem Warenkorb), nur der Recommendation-Teil wird transplantiert.
   */
  _carryOverRecommendations(currentWrapper, newWrapper) {
    const newSlider = newWrapper.querySelector('cart-gift-slider');
    // Kein Slider im neuen Wrapper (z.B. leerer Warenkorb) -> nichts zu uebernehmen.
    if (!newSlider) return;

    // Verhindert, dass der frisch verbundene Slider die API-Recommendations erneut holt.
    newSlider.removeAttribute('data-recommendations-url');

    const newSliderWrapper = newSlider.querySelector('.swiper-wrapper');
    if (!newSliderWrapper) return;

    // Frisch gerenderte Recommendation-Slides entfernen; die Live-Versionen kommen unten rein.
    newSliderWrapper
      .querySelectorAll('.swiper-slide[data-slide-kind="recommendation"]')
      .forEach((slide) => slide.remove());

    const currentSlider = currentWrapper.querySelector('cart-gift-slider');
    // Kein bestehender Slider (z.B. erster Refresh) -> keine Live-Slides zum Uebernehmen.
    if (!currentSlider) return;

    // Produkt-IDs im aktuellen Warenkorb (vom frischen Wrapper) fuer die Ausblend-Pruefung.
    // Bei deaktiviertem Section-Setting hide_products liefert Liquid das Attribut leer,
    // die Liste bleibt dann leer und es wird keine Slide ausgeblendet.
    const cartProductIds = (newWrapper.dataset.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    // Live-Recommendation-Slides per DOM-Move uebernehmen (erhaelt die geladenen Bilder).
    // Slides, deren Produkt inzwischen im Warenkorb liegt, werden ausgelassen (= entfernt).
    currentSlider
      .querySelectorAll('.swiper-slide[data-slide-kind="recommendation"]')
      .forEach((slide) => {
        if (cartProductIds.includes(slide.dataset.productId)) return;
        newSliderWrapper.appendChild(slide);
      });

    // Heading einblenden, sobald der Slider Slides enthaelt (analog zu _loadRecommendations).
    if (newSliderWrapper.children.length > 0) {
      const sliderBlock = newSlider.closest('.mini-cart__gift-slider');
      const heading = sliderBlock ? sliderBlock.querySelector('.mini-cart__gift-slider-heading') : null;
      if (heading) heading.hidden = false;
    }
  }

  /**
   * Holt den aktuellen Panel-Inhalt (.js-drawer-wrapper) des Carts.
   * 1) Section-Rendering-API (leichtgewichtig)
   * 2) Fallback: komplette Cart-Seite laden und Drawer per #mini-cart extrahieren
   *    (falls die Section-API die layout-eingebundene Section nicht liefert)
   */
  async _fetchDrawerWrapper() {
    const cartUrl = window.themeVariables.routes.cartUrl;
    const sectionId = this.getAttribute('section');
    const parse = (html) => {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div;
    };

    // 1) Section Rendering API
    if (sectionId) {
      try {
        const res = await fetch(`${cartUrl}?section_id=${encodeURIComponent(sectionId)}`);
        if (res.ok) {
          const wrapper = parse(await res.text()).querySelector('.js-drawer-wrapper');
          if (wrapper) return wrapper;
        }
      } catch (error) {
        /* faellt unten auf Full-Page zurueck */
      }
    }

    // 2) Fallback: komplette Cart-Seite
    const res = await fetch(cartUrl);
    const doc = parse(await res.text());
    return doc.querySelector('#mini-cart .js-drawer-wrapper') || doc.querySelector('cart-drawer .js-drawer-wrapper');
  }

  /* =======================================================================
   * GWP-Step-Konfetti
   *
   * Jede Warenkorbaenderung rendert den Drawer-Inhalt neu (_rerenderCart).
   * Nur dort sind Alt- und Neu-Zustand der GWP-Steps direkt vergleichbar:
   * Wechselt ein Step von "active"/"upcoming" auf "reached", wurde die
   * Schwelle gerade ueberschritten -> zwei Konfetti-Bursts (Progressbar +
   * Step-spezifisches Ziel: Versandzeile bzw. Geschenk-Button).
   * ======================================================================= */

  // Liest die Zustaende aller GWP-Steps als Map data-step-index -> data-step-state.
  _readStepStates(root) {
    const states = {};
    if (!root) return states;
    root.querySelectorAll('.mini-cart__gwp-step').forEach((step) => {
      const index = step.dataset.stepIndex;
      if (index != null) states[index] = step.dataset.stepState || '';
    });
    return states;
  }

  // Steps, die im neuen DOM auf "reached" gewechselt sind (und vorher bekannt waren).
  _diffNewlyReached(previousStates, root) {
    const newlyReached = [];
    if (!root) return newlyReached;
    root.querySelectorAll('.mini-cart__gwp-step').forEach((step) => {
      const previousState = previousStates[step.dataset.stepIndex];
      // Nur feuern, wenn der Step vorher bekannt UND nicht bereits erreicht war
      // (verhindert Konfetti beim reinen Oeffnen ohne Aenderung).
      if (step.dataset.stepState === 'reached' && previousState && previousState !== 'reached') {
        newlyReached.push(step);
      }
    });
    return newlyReached;
  }

  _celebrateNewlyReachedSteps(previousStates) {
    if (typeof confetti !== 'function') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const root = this.querySelector('.js-drawer-wrapper');
    if (!root) return;

    const newlyReached = this._diffNewlyReached(previousStates, root);
    if (!newlyReached.length) return;

    // Nach dem naechsten Layout feuern, damit die Ziel-Positionen stimmen
    // (der Drawer wird beim Add-to-Cart evtl. gerade erst eingeblendet).
    requestAnimationFrame(() => {
      newlyReached.forEach((step) => this._fireStepConfetti(step, root));
    });
  }

  // Zwei Bursts pro Step: aus der Progressbar-Mitte und aus dem Step-Ziel.
  _fireStepConfetti(stepEl, root) {
    this._launchConfettiFrom(root.querySelector('.mini-cart__gwp-progress'));

    let target = null;
    if (stepEl.dataset.stepType === 'shipping') {
      // Versandkostenzeile im Footer.
      target = root.querySelector('[cart-shipping]');
    } else {
      // Hinzufuegen-Button der zugehoerigen Geschenk-Slide.
      const productId = stepEl.dataset.giftProductId;
      if (productId) {
        const slide = root.querySelector(`.mini-cart__gift-slide[data-product-id="${productId}"]`);
        if (slide) target = slide.querySelector('.mini-cart__gift-slide-button') || slide;
      }
    }
    this._launchConfettiFrom(target);
  }

  // Konfetti-Burst aus der Bildschirmmitte des uebergebenen Elements.
  _launchConfettiFrom(element) {
    if (!element || typeof confetti !== 'function') return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const origin = {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    };
    this._confetti()({
      particleCount: 60,
      spread: 70,
      startVelocity: 30,
      gravity: 1.1,
      ticks: 160,
      origin,
      disableForReducedMotion: true,
    });
  }

  // Konfetti-Instanz mit eigenem Canvas, damit die Partikel ueber dem Drawer liegen
  // (der globale canvas-confetti-Canvas nutzt z-index 100 und bliebe hinter dem Drawer).
  _confetti() {
    if (this._confettiInstance) return this._confettiInstance;
    const canvas = document.createElement('canvas');
    canvas.className = 'cart-drawer-confetti-canvas';
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '2147483647',
    });
    document.body.appendChild(canvas);
    this._confettiInstance = confetti.create(canvas, { resize: true, useWorker: true });
    return this._confettiInstance;
  }

  /* =======================================================================
   * GWP-Text: Success-/Progress-Uebergang
   *
   * Standardmaessig zeigt der Server bereits die Progress-Message des aktiven
   * Steps. Wurde durch eine Warenkorbaenderung ein Step neu erreicht, blenden
   * wir zunaechst dessen Success-Message ein und wechseln nach GWP_SUCCESS_MS
   * per Slide auf die (bereits gerenderte) naechste Progress-Message.
   * Mehrere gleichzeitig erreichte Steps werden sequentiell abgespielt.
   * ======================================================================= */

  // Alle laufenden Message-Timer abbrechen (vor jedem Re-Render).
  _clearGwpMessageTimers() {
    if (this._gwpMessageTimers) {
      this._gwpMessageTimers.forEach((timer) => clearTimeout(timer));
    }
    this._gwpMessageTimers = [];
  }

  _animateGwpMessage(newlyReachedSteps) {
    const textEl = this.querySelector('.mini-cart__gwp-text');
    if (!textEl) return;

    const successMessages = newlyReachedSteps
      .map((step) => (step.dataset.successMessage || '').trim())
      .filter(Boolean);

    // Kein Step neu erreicht -> kein Slide, der Text wurde bereits still aktualisiert.
    if (!successMessages.length) return;

    // Abschluss-Zustand: Der Server markiert die Progressbar mit data-gwp-complete,
    // wenn der letzte Step erreicht wurde, und rendert sie initial ausgeblendet.
    const progress = this.querySelector('.mini-cart__gwp-progress');
    const isComplete = progress && progress.dataset.gwpComplete === 'true';

    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Im Abschluss-Fall nur die Success-Message(s) abspielen (keine Complete-Folge-
    // meldung) und die Progressbar fuer die Zelebration kurz einblenden. Normalerweise
    // wird nach den Success-Messages auf die naechste Progress-Message gewechselt.
    let sequence;
    if (isComplete) {
      progress.hidden = false;
      sequence = successMessages;
    } else {
      // Zieltext ist die vom Server gerenderte (naechste) Progress-Message.
      const progressText = textEl.textContent.trim();
      sequence = successMessages.concat(progressText);
    }

    // Nach der letzten Nachricht: im Abschluss-Fall die Progressbar wieder ausblenden.
    const finish = () => {
      if (isComplete) progress.hidden = true;
    };

    // Erste Success-Message sofort zeigen, dann durch die restliche Sequenz sliden.
    textEl.textContent = sequence[0];

    let index = 1;
    const showNext = () => {
      // Alle Nachrichten gezeigt -> Sequenz abschliessen.
      if (index >= sequence.length) {
        finish();
        return;
      }
      const next = sequence[index];
      index += 1;
      this._slideGwpText(textEl, next, reduceMotion, () => {
        this._gwpMessageTimers.push(setTimeout(showNext, GWP_SUCCESS_MS));
      });
    };

    this._gwpMessageTimers.push(setTimeout(showNext, GWP_SUCCESS_MS));
  }

  // Aktuelle Nachricht nach links rausfliegen lassen, neue von rechts reinholen.
  _slideGwpText(textEl, newText, reduceMotion, done) {
    if (reduceMotion) {
      textEl.textContent = newText;
      if (done) done();
      return;
    }

    textEl.classList.add('mini-cart__gwp-text--out-left');
    this._gwpMessageTimers.push(
      setTimeout(() => {
        textEl.textContent = newText;
        // Startzustand rechts (ohne Transition) setzen, dann in den Normalzustand animieren.
        textEl.classList.remove('mini-cart__gwp-text--out-left');
        textEl.classList.add('mini-cart__gwp-text--in-right');
        // Reflow erzwingen, damit der Startzustand vor dem Zuruecknehmen greift.
        void textEl.offsetWidth;
        textEl.classList.remove('mini-cart__gwp-text--in-right');
        if (done) done();
      }, GWP_SLIDE_MS)
    );
  }
}

if (window.customElements && !customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* =========================================================================
 * <cart-gift-slider> — Gift-with-Purchase- & Recommendation-Slider
 *
 * Markup kommt komplett aus sections/cart-drawer-v2.liquid (Swiper-Slides via
 * snippets/cart-drawer-v2-slider-item.liquid). Die Component uebernimmt nur:
 *   - Swiper-Init (Peek via numerischem slidesPerView, freeMode, Scrollbar-Indikator)
 *   - Klick-Delegation auf die ATC-Buttons (Gifts mit GWP-Properties)
 *   - Auto-Remove von Gifts, deren Schwelle nicht mehr erreicht ist
 *     (Line-Keys via data-remove-line-keys aus dem Liquid-Render)
 *   - Nachladen der Shopify-API-Recommendations: dieselbe Section wird ueber
 *     data-recommendations-url (Product-Recommendations-URL) erneut serverseitig
 *     gerendert; von dort werden nur die fertigen Slide-Nodes uebernommen.
 *     (Bewusst nicht ueber <sht-prd-recom>: das Element ist nur auf
 *     Produktseiten registriert, der Cart-Drawer laeuft global.)
 * ========================================================================= */
class CartGiftSlider extends HTMLElement {
  connectedCallback() {
    this._initSwiper();

    this._onClick = (event) => {
      const button = event.target.closest('.js-gift-slider-atc');
      if (button && this.contains(button)) this._onAddToCart(button);
    };
    this.addEventListener('click', this._onClick);

    this._onChange = (event) => {
      const select = event.target.closest('.js-gift-slider-variant-select');
      if (select && this.contains(select)) this._onVariantChange(select);
    };
    this.addEventListener('change', this._onChange);

    this._loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
    if (this._swiper) {
      this._swiper.destroy();
      this._swiper = null;
    }
  }

  _initSwiper() {
    const container = this.querySelector('.swiper');
    if (!container || typeof Swiper === 'undefined') return;

    // Angeschnittener Slide (Peek) signalisiert, dass noch mehr folgt.
    // Menge ueber data-slides-per-view konfigurierbar (2.5 / 3.5), Default 2.5.
    const slidesPerView = parseFloat(this.dataset.slidesPerView) || 2.5;

    // Scrollbar und Pfeil-Buttons liegen ausserhalb des .swiper-Containers in der
    // Controls-Zeile; daher per Element-Referenz (nicht per Selektor-String) uebergeben.
    const scrollbarEl = this.querySelector('.swiper-scrollbar');
    const prevEl = this.querySelector('.mini-cart__gift-slider-arrow--prev');
    const nextEl = this.querySelector('.mini-cart__gift-slider-arrow--next');

    this._swiper = new Swiper(".swiper", {
      slidesPerView: slidesPerView,
      spaceBetween: 12,
      // Ruheabstand von 20px hinter dem letzten Slide (analog zum Theme-Standard,
      // .drawer__content-Padding). Erweitert nur den Scroll-Endpunkt – die Slides
      // laufen beim aktiven Swipen weiterhin bis ganz an den Rand.
      slidesOffsetAfter: 40,
      freeMode: true,
      watchOverflow: true,
      // Normales (vertikales) Mausrad scrollt den Slider horizontal; an den Enden
      // gibt releaseOnEdges das Scrollen an den Drawer-Container (#mini-cart) zurueck.
      mousewheel: {
        forceToAxis: false,
        releaseOnEdges: true,
      },
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
        hide: false,
      },
      // Klickbare Pfeile zusaetzlich zum Ziehen; Swiper deaktiviert sie an den
      // Raendern automatisch (swiper-button-disabled).
      navigation: {
        prevEl: prevEl,
        nextEl: nextEl,
      },
    });
  }

  // Uebernimmt die im Reco-Dropdown gewaehlte Variante auf den ATC-Button der Slide
  // und aktualisiert die Preis-/Streichpreisanzeige aus den Data-Attributen der Option.
  _onVariantChange(select) {
    const slide = select.closest('.mini-cart__gift-slide');
    if (!slide) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    const button = slide.querySelector('.js-gift-slider-atc');
    if (button) button.dataset.variantId = option.value;

    const priceEl = slide.querySelector('.js-gift-slider-price');
    if (priceEl) priceEl.textContent = option.dataset.price || '';

    const compareEl = slide.querySelector('.js-gift-slider-compare');
    if (compareEl) {
      const compare = option.dataset.compareAtPrice || '';
      compareEl.textContent = compare;
      compareEl.hidden = compare === '';
    }

    const imageEl = slide.querySelector('.mini-cart__gift-slide-image');
    if (imageEl && option.dataset.image) {
      imageEl.src = option.dataset.image;
      if (option.dataset.imageSrcset) imageEl.srcset = option.dataset.imageSrcset;
    }
  }

  async _onAddToCart(button) {
    if (button.disabled) return;
    button.disabled = true;

    const items = [{ id: parseInt(button.dataset.variantId), quantity: 1 }];
    if (button.dataset.isGift) {
      items[0].properties = { __addedFromGiftSection: 'true', _GWP: 'true', __gwpThreshold: button.dataset.giftThreshold };
    }

    try {
      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: JSON.stringify({ items }),
        credentials: 'same-origin',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      
      button.disabled = false;
    } catch (error) {
      console.warn('[cart-drawer-v2] gift slider add failed', error);
      button.disabled = false;
    }
  }

  // Shopify-API-Recommendations nachladen: Section-Rerender via Product-Recommendations-URL,
  // dort ist recommendations.performed true -> Slides inkl. API-Produkten (serverseitig
  // dedupliziert und limitiert). Es werden nur fertige DOM-Nodes uebernommen.
  async _loadRecommendations() {
    const url = this.dataset.recommendationsUrl;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const parsed = document.createElement('div');
      parsed.innerHTML = await response.text();

      const fetchedWrapper = parsed.querySelector('cart-gift-slider .swiper-wrapper');
      const ownWrapper = this.querySelector('.swiper-wrapper');
      if (!fetchedWrapper || !ownWrapper) return;

      ownWrapper.replaceChildren(...fetchedWrapper.children);

      if (ownWrapper.children.length > 0) {
        const sliderBlock = this.closest('.mini-cart__gift-slider');
        const heading = sliderBlock ? sliderBlock.querySelector('.mini-cart__gift-slider-heading') : null;
        if (heading) heading.hidden = false;
      }

      if (this._swiper) {
        this._swiper.update();
        // Scrollbar-Laenge und Overflow-Lock nach dem Nachladen neu berechnen.
        this._swiper.scrollbar?.updateSize();
      }
    } catch (error) {
      console.warn('[cart-drawer-v2] recommendations load failed', error);
    }
  }
}

if (!customElements.get('cart-gift-slider')) {
  customElements.define('cart-gift-slider', CartGiftSlider);
}

/* =========================================================================
 * <a is="line-item-quantity-remove-button"> — self-contained (unveraendert)
 * ========================================================================= */
class LineItemQuantityRemoveButton extends HTMLAnchorElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick.bind(this));
  }
  get for() {
    return this.getAttribute('for');
  }
  _onClick(event) {
    event.preventDefault();
    const lineItemQuantity = document.querySelector(`#${this.for}`);
    if (!lineItemQuantity) return;
    lineItemQuantity.dispatchEvent(
      new CustomEvent('remove-link-clicked', { bubbles: true, detail: { href: this.href } })
    );
  }
}

if (!customElements.get('line-item-quantity-remove-button')) {
  customElements.define('line-item-quantity-remove-button', LineItemQuantityRemoveButton, { extends: 'a' });
}

/* =========================================================================
 * <line-item-quantity> — Basis CustomHTMLElement ersetzt durch HTMLElement
 *                        + eigene Event-Delegation
 * ========================================================================= */
class LineItemQuantity extends HTMLElement {
  connectedCallback() {
    // Verzoegerung, damit schnelle +/- Klicks zu EINEM Request mit der Endmenge werden.
    this._quantityDebounceMs = 450;
    this._pendingTimer = null;

    this._onClick = (event) => {
      const link = event.target.closest('a');
      if (link && this.contains(link)) this._onQuantityLinkClicked(event, link);
    };
    this._onChange = (event) => {
      const input = event.target.closest('input');
      if (input && this.contains(input)) this._onQuantityChanged(event, input);
    };
    this._onRemove = this._onRemoveLinkClicked.bind(this);

    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.addEventListener('remove-link-clicked', this._onRemove);
  }

  disconnectedCallback() {
    this._clearPending();
  }

  get _input() {
    return this.querySelector('input');
  }

  _clearPending() {
    if (this._pendingTimer) {
      clearTimeout(this._pendingTimer);
      this._pendingTimer = null;
    }
  }

  _onRemoveLinkClicked(event) {
    event.preventDefault();
    this._clearPending();
    this._updateFromLink(event.detail.href);
  }

  // +/- Buttons: Menge lokal akkumulieren + optimistisch anzeigen, Fetch verzoegern.
  // 3x schnell "+" -> Anzeige +3, danach EIN Request mit der Endmenge.
  _onQuantityLinkClicked(event, target) {
    event.preventDefault();
    const input = this._input;
    if (!input) {
      this._updateFromLink(target.href);
      return;
    }

    // Richtung (+1/-1) aus der Link-Zielmenge vs. gerendertem Ausgangswert bestimmen.
    // getAttribute('value') bleibt stabil, auch wenn wir input.value optimistisch aendern.
    const linkQuantity = parseInt(new URL(target.href, window.location.origin).searchParams.get('quantity'));
    const baseQuantity = parseInt(input.getAttribute('value'));
    let step = 0;
    if (!Number.isNaN(linkQuantity) && !Number.isNaN(baseQuantity)) {
      step = linkQuantity > baseQuantity ? 1 : -1;
    }

    const current = parseInt(input.value);
    const next = Math.max(0, (Number.isNaN(current) ? baseQuantity : current) + step);

    // Optimistisch anzeigen (kein change-Event -> loest keinen sofortigen Fetch aus).
    input.value = String(next);
    input.size = Math.max(String(next).length + 1, 2);

    this._scheduleQuantityUpdate();
  }

  _scheduleQuantityUpdate() {
    this._clearPending();
    this._pendingTimer = setTimeout(() => {
      this._pendingTimer = null;
      const input = this._input;
      if (!input) return;
      const quantity = parseInt(input.value);
      const line = input.getAttribute('data-line');
      this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${quantity}&line=${line}`);
    }, this._quantityDebounceMs);
  }

  // Direkte Eingabe (Tippen): sofort, aber einen ausstehenden +/- Timer verwerfen.
  _onQuantityChanged(event, target) {
    this._clearPending();
    this._updateFromLink(
      `${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute('data-line')}`
    );
  }

  async _updateFromLink(link) {
    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.href = link;
      return;
    }
    const changeUrl = new URL(link, `https://${window.themeVariables.routes.host}`);
    const searchParams = changeUrl.searchParams;
    const line = searchParams.get('line');
    const id = searchParams.get('id');
    const quantity = parseInt(searchParams.get('quantity'));

    this.dispatchEvent(
      new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: quantity } })
    );

    const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
      body: JSON.stringify({ line, id, quantity }),
      credentials: 'same-origin',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      const cartContent = await response.json();
      this.dispatchEvent(
        new CustomEvent('line-item-quantity:change:end', {
          bubbles: true,
          detail: { cart: cartContent, newLineQuantity: quantity },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cartContent, replacementDelay: quantity === 0 ? 600 : 750 },
        })
      );
    } else {
      this.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));
    }
  }
}

if (!customElements.get('line-item-quantity')) {
  customElements.define('line-item-quantity', LineItemQuantity);
}

/* =========================================================================
 * <line-item> — self-contained (native element.animate; unveraendert)
 * ========================================================================= */
class LineItem extends HTMLElement {
  connectedCallback() {
    this.lineItemLoader = this.querySelector('.line-item__loader');
    this.addEventListener('line-item-quantity:change:start', this._onQuantityStart.bind(this));
    this.addEventListener('line-item-quantity:change:end', this._onQuantityEnd.bind(this));
  }
  _onQuantityStart() {
    // Nur den Lade-Spinner einblenden (kein Haken mehr).
    if (this.lineItemLoader) this.lineItemLoader.hidden = false;
  }
  async _onQuantityEnd(event) {
    if (event?.detail?.cart['item_count'] === 0) return;
    if (event?.detail?.newLineQuantity === 0) {
      // Menge 0 -> Zeile ausblenden und entfernen.
      await this.animate(
        { opacity: [1, 0], height: [`${this.clientHeight}px`, 0] },
        { duration: 300, easing: 'ease' }
      ).finished;
      this.remove();
    } else if (this.lineItemLoader) {
      this.lineItemLoader.hidden = true;
    }
  }
}

if (!customElements.get('line-item')) {
  customElements.define('line-item', LineItem);
}

/* =========================================================================
 * <input is="input-number"> — self-contained (unveraendert)
 * ========================================================================= */
class InputNumber extends HTMLInputElement {
  connectedCallback() {
    this.addEventListener('input', this._onValueInput.bind(this));
    this.addEventListener('change', this._onValueChanged.bind(this));
    this.addEventListener('keydown', this._onKeyDown.bind(this));
  }
  get quantity() {
    return parseInt(this.value);
  }
  set quantity(quantity) {
    const isNumeric =
      (typeof quantity === 'number' || (typeof quantity === 'string' && quantity.trim() !== '')) &&
      !isNaN(quantity);
    if (quantity === '') return;
    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }
    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    this.size = Math.max(this.value.length + 1, 2);
  }
  _onValueInput() {
    this.quantity = this.value;
  }
  _onValueChanged() {
    if (this.value === '') this.quantity = 1;
  }
  _onKeyDown(event) {
    event.stopPropagation();
    if (event.key === 'ArrowUp') {
      this.quantity = this.quantity + 1;
    } else if (event.key === 'ArrowDown') {
      this.quantity = this.quantity - 1;
    }
  }
}

if (!customElements.get('input-number')) {
  customElements.define('input-number', InputNumber, { extends: 'input' });
}
